import {
  ProductCondition,
  ProductSortType,
  SalesChannel,
  SizeFilterGender,
  SizeFilterSizeUnit,
} from '@goatapp/protos-gen-node/buying/consumer_search/api/v1/server_pb'
import Cookies from 'js-cookie'
import { DEFAULT_CURRENCY_CODE, STORAGE } from 'shared/enums/SitePreferences'
import { SearchState } from 'shared/types/Search'
export { SizeFilterGender } from '@goatapp/protos-gen-node/buying/consumer_search/api/v1/server_pb'

export type CSSearchState = {
  index?: string
  page?: number
  query?: string
  product_type?: string[]
  range?: {
    [key: string]: {
      min: string
      max: string
    }
  }
  refinementList?: Record<string, string[]>
}

type AlgoliaFilters = {
  activity?: string[]
  brand?: string[]
  category?: string[]
  collection?: string
  country?: string
  currency: string
  gender?: string[]
  page?: number
  pageLimit?: string
  pricemax?: number
  pricemin?: number
  query?: string
  range?: {
    [key: string]: {
      min: number
      max: number
    }
  }
  refinementList?: {
    [key: string]: string[]
  }
  silhouette?: string[]
  sortType: number
  type?: string[]
}

export interface ProductFilter {
  activities: string[]
  brands: string[]
  categories: string[]
  colors: string[]
  conditions: ProductCondition[]
  designers: string[]
  genders: string[]
  productTypes: string[]
  releaseDateEnd: number | undefined
  releaseDateStart: number | undefined
  silhouettesList: string[]
  sizes: {
    [key: string]: string | number
  }[]
}

export type ConsumerSearchFiltersObj = {
  collectionSlug: string
  country: string
  currency: string
  includeAggregations: string
  pageLimit: string
  pageNumber: string
  priceCentsMax: number | string
  priceCentsMin: number | string
  productFilter: ProductFilter
  queryString: string
  salesChannelId: string
  sortType: string
  vector: string
}

export type ConsumerSearchFilters = Omit<
  ConsumerSearchFiltersObj,
  'priceCentsMax' | 'priceCentsMin' | 'productFilter'
> & {
  priceCentsMax: string
  priceCentsMin: string
  productFilter: string
  preFilters: string
}

type GetConsumerSearchFiltersParams = {
  cookies: Partial<Record<string, string>>
  isClientOnly?: boolean
  pageLimit?: string
  queryParams: Record<string, string>
  searchState: SearchState
}

const APPAREL_PRODUCT_CATEGORIES = ['tops', 'bottoms', 'outerwear', 'dresses', 'belts']

export const PRODUCT_CONDITION_MAP: {
  [key: string]: {
    name: string
    value: ProductCondition
    key: string
  }
} = {
  new_no_defects: {
    name: 'New',
    value: ProductCondition.NEW_NO_DEFECTS,
    key: 'new_no_defects',
  },
  new_with_defects: {
    name: 'New with Defect',
    value: ProductCondition.NEW_WITH_DEFECTS,
    key: 'new_with_defects',
  },
  goat_clean: {
    name: 'GOAT Clean',
    value: ProductCondition.GOAT_CLEAN,
    key: 'goat_clean',
  },
  used: {
    name: 'Used',
    value: ProductCondition.USED,
    key: 'used',
  },
}

export const SIZE_UNIT_MAP: Record<string, number> = {
  US: SizeFilterSizeUnit.SIZE_FILTER_SIZE_UNIT_US,
  UK: SizeFilterSizeUnit.SIZE_FILTER_SIZE_UNIT_UK,
  IT: SizeFilterSizeUnit.SIZE_FILTER_SIZE_UNIT_IT,
  FR: SizeFilterSizeUnit.SIZE_FILTER_SIZE_UNIT_FR,
  EU: SizeFilterSizeUnit.SIZE_FILTER_SIZE_UNIT_EU,
  JP: SizeFilterSizeUnit.SIZE_FILTER_SIZE_UNIT_JP,
}

export const SORT_FILTER_MAP: {
  [key: string]: {
    name: string
    value: ProductSortType
    key: string
  }
} = {
  popularity: {
    name: 'Relevance',
    value: ProductSortType.POPULARITY_DESC,
    key: 'popularity',
  },
  trending: {
    name: 'Trending',
    value: ProductSortType.TRENDING_PURCHASE_DESC,
    key: 'trending',
  },
  recently_released: {
    name: 'Newest',
    value: ProductSortType.RELEASE_DATE_DESC,
    key: 'recently_released',
  },
  price_low_high: {
    name: 'Price (Low - High)',
    value: ProductSortType.PRICE_ASC,
    key: 'price_low_high',
  },
  price_high_low: {
    name: 'Price (High - Low)',
    value: ProductSortType.PRICE_DESC,
    key: 'price_high_low',
  },
}

export const consumerSearchConversionSizes = (
  sizes: { [key: string]: string[] }[],
  types: string[] | number[],
) => {
  const allSizes: {
    [key: string]: string | number
  }[] = []
  sizes.forEach((size) => {
    const key = Object.keys(size)[0]
    const sizeValue = size[key]
    const [, unit, gender] = (key || '').toLowerCase().split('_')

    sizeValue.forEach((size) => {
      types.forEach((type) => {
        allSizes.push({
          value: size,
          gender:
            gender === 'men'
              ? SizeFilterGender.SIZE_FILTER_GENDER_MEN
              : gender === 'women'
              ? SizeFilterGender.SIZE_FILTER_GENDER_WOMEN
              : SizeFilterGender.SIZE_FILTER_GENDER_YOUTH,
          type: type || '',
          unit: (unit || type === 'apparel' ? '' : 'us').toUpperCase(),
        })
      })
    })
  })

  return allSizes
}

export const getAllfilters = ({
  activities = '',
  brands = '',
  categories = '',
  collection = '',
  colors = '',
  conditions = '',
  genders = '',
  priceMax = '',
  priceMin = '',
  queryString = '',
  releaseDateEnd = '',
  releaseDateStart = '',
  releaseYears = '',
  silhouettes = '',
  types = '',
  vector = '',
}: Record<string, string> = {}) => {
  const filtersParams = {
    activities: getSearchFilterArray(activities),
    brands: getSearchFilterArray(brands),
    categories: getSearchFilterArray(categories),
    colors: getSearchFilterArray(colors),
    conditions: getSearchFilterArray(conditions).map(
      (condition) => PRODUCT_CONDITION_MAP[condition].value,
    ),
    genders: getSearchFilterArray(genders),
    productTypes: getSearchFilterArray(types),
    releaseDateEnd: getSearchFilterArray(releaseDateEnd)[0]
      ? getTimeStamps({
          year: parseInt(getSearchFilterArray(releaseDateEnd)[0]),
        })
      : undefined,
    releaseDateStart: getSearchFilterArray(releaseDateStart)[0]
      ? getTimeStamps({
          year: parseInt(getSearchFilterArray(releaseDateStart)[0]),
        })
      : undefined,
    releaseYears: getSearchFilterArray(releaseYears),
    silhouettesList: getSearchFilterArray(silhouettes),
  }

  const query = {
    collectionSlug: collection || '',
    country: Cookies.get(STORAGE.COUNTRY) || 'US',
    currency: Cookies.get(STORAGE.CURRENCYCODE) || 'USD',
    includeAggregations: 'true',
    pageLimit: '1',
    pageNumber: '1',
    priceCentsMax:
      getSearchFilterArray(priceMax)[0] !== 'NaN' ? getSearchFilterArray(priceMax)[0] : undefined,
    priceCentsMin:
      getSearchFilterArray(priceMin)[0] !== 'NaN' ? getSearchFilterArray(priceMin)[0] : undefined,
    productFilter: JSON.stringify(filtersParams),
    queryString,
    salesChannelId: '2',
    sortType: '1',
    vector: vector || '',
  }

  return query
}

/**
 * Splits a comma-separated query string into an array of trimmed strings.
 */
export const getSearchFilterArray = (query: string): string[] => {
  if (!query) return []
  return query
    .split(',')
    .map((f) => f.trim())
    .filter(Boolean)
}

/**
 * Returns the sort type for a given string.
 */
export const getSortType = (type: string) => {
  if (type?.toLocaleLowerCase().includes('newest')) {
    return 2
  } else if (type?.toLocaleLowerCase().includes('price_desc')) {
    return 4
  } else if (type?.toLocaleLowerCase().includes('price_asc')) {
    return 3
  } else if (type?.toLocaleLowerCase().includes('trending')) {
    return 5
  }
  return 1
}

export const getTimeStamps = ({
  year,
  month = 0,
  day = 1,
}: {
  year: number
  month?: number
  day?: number
}) => {
  return new Date(year, month, day).getTime()
}

export function getConsumerSearchFilters({
  cookies = {},
  isClientOnly = false,
  pageLimit = '30',
  queryParams = {},
  searchState,
}: GetConsumerSearchFiltersParams): ConsumerSearchFilters | ConsumerSearchFiltersObj {
  const params = {
    country: cookies.country,
    currency: cookies.currency || DEFAULT_CURRENCY_CODE,
    pageLimit: pageLimit,
    query: queryParams.query || searchState?.query,
    pricemin: Number(queryParams.min_price) * 100,
    pricemax: Number(queryParams.max_price) * 100,
    sortType: getConsumerSearchSortType((queryParams.sortBy || searchState?.index || '') as string),
    type: [],
    ...searchState,
  }

  const csSearchFilters = toConsumerSearchFilters(params)

  return isClientOnly
    ? csSearchFilters
    : {
        ...csSearchFilters,
        priceCentsMax: `${csSearchFilters.priceCentsMax}`,
        priceCentsMin: `${csSearchFilters.priceCentsMin}`,
        productFilter: JSON.stringify(csSearchFilters.productFilter),
      }
}

export const getConsumerSearchSortType = (sortKey: string) => {
  if (sortKey.includes('newest')) {
    return SORT_FILTER_MAP.recently_released.value
  }

  if (sortKey.includes('price_asc')) {
    return SORT_FILTER_MAP.price_low_high.value
  }

  if (sortKey.includes('price_desc')) {
    return SORT_FILTER_MAP.price_high_low.value
  }

  if (sortKey.includes('trending')) {
    return SORT_FILTER_MAP.trending.value
  }

  return SORT_FILTER_MAP.popularity.value
}

export const getClientConsumerSearchFilters = (
  searchState: SearchState,
  query: Record<string, string>,
  preFilters: Record<string, boolean>,
): ConsumerSearchFilters => {
  const currentSearchParams = getConsumerSearchFilters({
    cookies: {
      currency: Cookies.get(STORAGE.CURRENCY) as string,
      country: Cookies.get(STORAGE.COUNTRY) as string,
    },
    isClientOnly: true,
    pageLimit: '30',
    queryParams: query || {},
    searchState: searchState,
  }) as ConsumerSearchFiltersObj

  if (query?.max_price) {
    currentSearchParams.priceCentsMax = parseFloat(query.max_price) * 100
  } else if (searchState?.range?.lowest_price_cents_usd?.max) {
    currentSearchParams.priceCentsMax = searchState.range.lowest_price_cents_usd.max
  }

  if (query?.min_price) {
    currentSearchParams.priceCentsMin = parseFloat(query.min_price) * 100
  } else if (searchState?.range?.lowest_price_cents_usd?.min) {
    currentSearchParams.priceCentsMin = searchState.range.lowest_price_cents_usd.min
  }

  if (searchState?.index || query?.sortBy) {
    currentSearchParams.sortType = getSortType(query.sortBy || searchState?.index || '').toString()
  }

  if (query.page) {
    currentSearchParams.pageNumber = query.page
  }

  const productFilter: ProductFilter = {
    activities: [],
    brands: [],
    categories: [],
    colors: [],
    conditions: [],
    designers: [],
    genders: [],
    productTypes: [],
    releaseDateEnd: undefined,
    releaseDateStart: undefined,
    silhouettesList: [],
    sizes: [],
  }

  productFilter.productTypes = getSearchFilterArray(
    searchState?.product_type?.join(',') || query.product_type,
  )

  productFilter.brands = getSearchFilterArray(
    query.brand ||
      (searchState?.refinementList?.brand || searchState?.refinementList?.brand_name)?.join(',') ||
      '',
  )
  productFilter.categories = searchState.refinementList?.product_category?.includes('clothing')
    ? [
        'apparel',
        ...(searchState.refinementList.product_category.filter((c) => c !== 'clothing') || []),
      ]
    : searchState.refinementList?.product_category || []
  productFilter.colors = getSearchFilterArray(query.color)
  productFilter.conditions = getSearchFilterArray(query.condition).map(
    (condition) => PRODUCT_CONDITION_MAP[condition].value,
  )
  productFilter.genders = searchState?.refinementList?.single_gender || []
  currentSearchParams.collectionSlug = searchState?.refinementList?.collection_slugs?.[0] || ''

  if (query.year_max) {
    productFilter.releaseDateEnd = getTimeStamps({
      year: parseInt(query.year_max as string),
    })
  }
  if (query.year_min) {
    productFilter.releaseDateStart = getTimeStamps({
      year: parseInt(query.year_min as string),
    })
  }
  productFilter.silhouettesList = getSearchFilterArray(
    query.model || searchState?.refinementList?.silhouette?.join(',') || '',
  )
  productFilter.activities = getSearchFilterArray(
    searchState?.refinementList?.activity?.join(',') || '',
  )
  productFilter.designers = getSearchFilterArray(
    searchState?.refinementList?.designer?.join(',') || '',
  )
  const currentSize: Record<string, string[]>[] = []
  if (query.size_women) {
    currentSize.push({ _us_women: getSearchFilterArray(query.size_women) })
  }

  if (query.size_men) {
    currentSize.push({ _us_men: getSearchFilterArray(query.size_men) })
  }

  if (query.size_youth) {
    currentSize.push({ _us_youth: getSearchFilterArray(query.size_youth) })
  }

  // sizes
  if (currentSize.length > 0) {
    productFilter.sizes = consumerSearchConversionSizes(
      currentSize,
      productFilter.categories?.[0] === 'apparel' ? APPAREL_PRODUCT_CATEGORIES : ['sneakers'],
    )
  }

  return {
    ...currentSearchParams,
    priceCentsMax: `${currentSearchParams.priceCentsMax}`,
    priceCentsMin: `${currentSearchParams.priceCentsMin}`,
    productFilter: JSON.stringify(productFilter),
    preFilters: JSON.stringify(preFilters),
  }
}

export function toConsumerSearchFilters({
  collection,
  country,
  currency,
  gender,
  page,
  pageLimit,
  pricemax,
  pricemin,
  query,
  range,
  refinementList,
  sortType,
}: AlgoliaFilters): ConsumerSearchFiltersObj {
  const categoryFilters = refinementList?.product_category?.includes('clothing')
    ? ['apparel', ...(refinementList.product_category.filter((c) => c !== 'clothing') || [])]
    : refinementList?.product_category || []
  const pricesCent =
    Object.keys(range || {}).filter((key) => key.includes('price'))[0] || 'lowest_price_cents_usd'
  const productTypeFilters = refinementList?.product_type || []
  const sizesKeys = Object.keys(refinementList || {}).filter((key) => key.includes('size_'))

  const filtersParams = {
    activities: refinementList?.activity || [],
    brands: refinementList?.brand || refinementList?.brand_name || [],
    categories: categoryFilters,
    colors: refinementList?.color || [],
    conditions: (refinementList?.shoe_condition || []).map(
      (condition: string) => PRODUCT_CONDITION_MAP[condition].value,
    ),
    designers: refinementList?.designer || [],
    genders: gender || refinementList?.single_gender || [],
    productTypes: productTypeFilters,
    releaseDateEnd: range?.release_year?.max
      ? getTimeStamps({ year: range?.release_year?.max })
      : undefined,
    releaseDateStart: range?.release_year?.min
      ? getTimeStamps({ year: range?.release_year?.min })
      : undefined,
    silhouettesList: refinementList?.silhouette || [],
    sizes:
      sizesKeys.length > 0
        ? consumerSearchConversionSizes(
            sizesKeys.map((refinement: string) => ({
              [refinement]: refinementList?.[refinement] || [],
            })),
            categoryFilters[0] === 'apparel' ? APPAREL_PRODUCT_CATEGORIES : ['sneakers'],
          )
        : [],
  }

  const queryString = {
    collectionSlug: refinementList?.collection_slugs?.[0] || collection || '',
    country: country || 'US',
    currency: currency || 'USD',
    includeAggregations: true.toString(),
    pageLimit: pageLimit || '30',
    pageNumber: (page || 1).toString(),
    priceCentsMax: pricemax || range?.[pricesCent]?.max || '',
    priceCentsMin: pricemin || range?.[pricesCent]?.min || '',
    productFilter: filtersParams,
    queryString: query || '',
    salesChannelId: `${SalesChannel.SALES_CHANNEL_FLIGHT_CLUB}`,
    sortType: sortType.toString(),
    vector: '',
  }

  return queryString
}

export const transformAggregationsToStructuredData = (buckets) => {
  if (buckets.length === 0) return {}

  const result = {
    brandsList: [],
    colorsList: [],
    gendersList: [],
    releaseYearsList: [],
    productCategoriesList: [],
    minimumPriceCents: null,
    maximumPriceCents: null,
    productConditionsList: [],
    sizesList: [],
    silhouettesList: [],
    designersList: [],
    seasonsList: [],
    underRetail: false,
    instantShip: false,
    hasNewInApparel: false,
  }

  const transformValuesList = (valuesList) =>
    valuesList.map((v) => ({ name: v.value, count: v.count, slug: v.slug }))

  const transformSizesBucketNested = (valuesList) => {
    return valuesList.map((categoryEntry) => {
      const categoryName = categoryEntry.value
      let categoryCount = 0

      const gendersList = []

      for (const genderBucket of categoryEntry.bucketsList || []) {
        if (genderBucket.bucketName !== 'genders') continue

        for (const genderEntry of genderBucket.valuesList || []) {
          let genderName = genderEntry.slug || genderEntry.value

          genderName = `US_${genderName}`

          let genderCount = 0
          const availableSizesMap = new Map()

          const sizesBucket = genderEntry.bucketsList?.find((b) => b.bucketName === 'sizes')

          for (const sizeEntry of sizesBucket?.valuesList || []) {
            const size = sizeEntry.sizeValue?.size
            const { count } = sizeEntry

            if (size) {
              genderCount += count
              categoryCount += count

              if (!availableSizesMap.has(size)) {
                availableSizesMap.set(size, {
                  name: size,
                  count,
                })
              } else {
                availableSizesMap.get(size).count += count
              }
            }
          }

          gendersList.push({
            name: genderName,
            count: genderCount,
            availableSizesList: [...availableSizesMap.values()].sort((a, b) => b.count - a.count),
          })
        }
      }

      return {
        name: categoryName,
        count: categoryCount,
        gendersList,
      }
    })
  }

  for (const bucket of buckets) {
    switch (bucket.bucketName) {
      case 'brands':
        result.brandsList = transformValuesList(bucket.valuesList)
        break
      case 'colors':
        result.colorsList = transformValuesList(bucket.valuesList)
        break
      case 'genders':
        result.gendersList = transformValuesList(bucket.valuesList)
        break
      case 'release_years':
        result.releaseYearsList = transformValuesList(bucket.valuesList)
        break
      case 'silhouettes':
        result.silhouettesList = transformValuesList(bucket.valuesList)
        break
      case 'conditions':
        result.productConditionsList = transformValuesList(bucket.valuesList)
        break
      case 'sizes':
        result.sizesList = transformSizesBucketNested(bucket.valuesList)
        break
      case 'price_min':
        if (
          bucket.valuesList?.[0]?.moneyValue &&
          typeof bucket.valuesList[0].moneyValue.amountCents === 'number'
        ) {
          bucket.valuesList[0].moneyValue.amountCents =
            bucket.valuesList[0].moneyValue.amountCents / 100
          result.minimumPriceCents = bucket.valuesList[0].moneyValue
        }
        break
      case 'price_max':
        if (
          bucket.valuesList?.[0]?.moneyValue &&
          typeof bucket.valuesList[0].moneyValue.amountCents === 'number'
        ) {
          bucket.valuesList[0].moneyValue.amountCents =
            bucket.valuesList[0].moneyValue.amountCents / 100
          result.maximumPriceCents = bucket.valuesList[0].moneyValue
        }
        break
      case 'categories':
        result.productCategoriesList = bucket.valuesList.map((category) => {
          const productTypes =
            category.bucketsList?.find((b) => b.bucketName === 'product_type')?.valuesList || []

          const productTypesList = productTypes.map((pt) => {
            let activities =
              pt.bucketsList?.find((b) => b.bucketName === 'activities')?.valuesList || []

            // If the product type is sneakers, we need to include the activities from the "archive_activities" bucket as well

            if (pt?.value?.toLowerCase() === 'sneakers') {
              const archiveActivities =
                pt.bucketsList?.find((b) => b.bucketName === 'archive_activities')?.valuesList || []
              activities = archiveActivities.filter((a) =>
                ['basketball', 'lifestyle', 'running', 'skateboarding'].includes(
                  a?.value?.toLowerCase(),
                ),
              )
              // add activity: to the value
              // this will make the difference between activities filters and level 3 taxonomy filters on the backend
              activities = activities.map((a) => ({
                ...a,
                value: `activity:${a.value}`,
              }))
            }
            return {
              name: pt.value,
              count: pt.count,
              activitiesList: transformValuesList(activities),
            }
          })

          return {
            name: category.value,
            count: category.count,
            productTypesList,
          }
        })

        result.hasNewInApparel = result.productCategoriesList.some(
          (category) => category.name === 'Apparel',
        )
        break

      case 'instant_ship':
        result.instantShip = Number.parseInt(bucket.valuesList?.[0]?.value || '0', 10) > 0
        break
      case 'under_retail':
        result.underRetail = Number.parseInt(bucket.valuesList?.[0]?.value || '0', 10) > 0
        break
      default:
        break
    }
  }

  return result
}
